import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { Observable, withLatestFrom } from 'rxjs';
import {
  ApiMedicationsSearchItemResponse,
  HealthConditions,
  PatientMedicalHistory,
  PatientMedicalHistoryFormValues,
} from 'src/app/core/models';
import { MedicationSearchService } from 'src/app/core/services';
import {
  FetchHealthConditions,
  FetchPatientMedicalHistory,
  UpdatePatientMedicalHistory,
} from 'src/app/core/store/medical-history/medical-history.actions';
import {
  MedicalHistoryState,
  PatientMedicalHistoryStrings,
} from 'src/app/core/store/medical-history/medical-history.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import { asFormControl } from 'src/app/core/utils/common';
import { validateAllFormFields } from 'src/app/core/utils/validators';
import { FormFieldOption } from 'src/app/shared/form-field/form-field.component';

@Component({
  selector: 'app-medical',
  templateUrl: './medical.component.html',
  styleUrls: ['./medical.component.scss'],
})
export class MedicalComponent implements OnInit {
  @Select(MedicalHistoryState.patientMedicalHistoryState) patientMedicalHistoryState$!: Observable<
    ApiStoreData<PatientMedicalHistory | null>
  >;

  @Select(MedicalHistoryState.patientMedicalHistoryStrings)
  patientMedicalHistoryStrings$!: Observable<PatientMedicalHistoryStrings>;

  @Select(MedicalHistoryState.healthConditionsState) healthConditionsState$!: Observable<
    ApiStoreData<HealthConditions | null>
  >;

  @Select(MedicalHistoryState.updateMedicalHistoryState) updateMedicalHistoryState$!: Observable<ApiStoreData<null>>;

  editMode: boolean = false;
  showError: boolean = false;
  isSearchingMedications: boolean = false;

  // TODO Add UI validation for invalid checkbox states
  medicalHistoryForm: FormGroup = new FormGroup({
    medications: new FormControl([]),

    medicalConditions: new FormControl([]),

    allergies: new FormControl([]),
  });

  initialValues: PatientMedicalHistoryFormValues = {
    medications: [],

    medicalConditions: [],

    allergies: [],
  };

  medicalConditionOptions: FormFieldOption[] = [];
  allergyOptions: FormFieldOption[] = [];
  medicationResults: ApiMedicationsSearchItemResponse[] = [];

  asFormControl = asFormControl;

  constructor(
    private store: Store,
    private medicationSearchService: MedicationSearchService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new FetchPatientMedicalHistory());
    this.store.dispatch(new FetchHealthConditions());

    this.patientMedicalHistoryState$.subscribe((medicalHistoryState) => {
      if (medicalHistoryState.data) {
        this.initialValues = {
          medications: medicalHistoryState.data.medications.map((m) => ({ label: m.name, value: m.ndc })),

          medicalConditions: medicalHistoryState.data.medicalConditions,

          allergies: medicalHistoryState.data.allergyConditions,
        };

        this.medicalHistoryForm.setValue(this.initialValues);
      }
    });

    this.healthConditionsState$.subscribe((healthConditionsState) => {
      if (healthConditionsState.data) {
        this.medicalConditionOptions = healthConditionsState.data.medicalConditions
          .map((mc) => ({
            label: mc,
            value: mc,
          }))
          .reverse();

        this.allergyOptions = healthConditionsState.data.allergyConditions
          .map((ac) => ({
            label: ac,
            value: ac,
          }))
          .reverse();
      }
    });
  }

  startEdit() {
    this.editMode = true;
  }

  endEdit() {
    this.editMode = false;
  }

  onSubmit() {
    if (!this.medicalHistoryForm.valid) {
      validateAllFormFields(this.medicalHistoryForm);
      return;
    }

    if (isEqual(this.medicalHistoryForm.value, this.initialValues)) {
      this.endEdit();
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }

    this.store
      .dispatch(new UpdatePatientMedicalHistory(this.medicalHistoryForm.value))
      .pipe(withLatestFrom(this.updateMedicalHistoryState$))
      .subscribe(([_, updateMedicalHistoryState]) => {
        if (updateMedicalHistoryState.error) {
          this.showError = true;
          window.scroll({ top: 0, left: 0, behavior: 'smooth' });
          return;
        }

        this.endEdit();
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        this.showError = false;
        this.messageService.clear('profileMessages');

        this.messageService.add({
          key: 'profileMessages',
          severity: 'success',
          detail: 'Medical history updated successfully.',
        });
      });
  }

  onCancel() {
    this.endEdit();
    this.showError = false;
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    this.medicalHistoryForm.reset(this.initialValues);
  }

  onSearchMedications(event: any) {
    // TODO Add error handling
    this.isSearchingMedications = true;

    this.medicationSearchService.searchMedications(event.query).subscribe((apiResults) => {
      this.medicationResults = apiResults;
      this.isSearchingMedications = false;
    });
  }
}
